<template>
  <div>
    <ValidationObserver ref="form">
      <div class="row border-bottom">
        <div class="input-left col-lg-3 col-4">
          <label>
              {{label}}
          </label>
          <span v-if="required" class="red-dot">*</span>
        </div>
        <div class="col-lg-9 col-8 pl-2">
          <ValidationProvider v-slot="{ errors, failed }"
                              :name="name"
                              :rules="rules"
          >
            <b-form-group>
              <div class="row">
                <div class="col-lg-9 col-10"
                    :class="{'col-12': isEditing}">
                  <b-form-input v-if="inputType === 'input'"
                                v-model="inputVal"
                                :state="state(errors)"
                                :disabled="!isEditing"
                                :type="type"
                                :fieldName="fieldName"
                                ref="input"
                  >
                  </b-form-input>
                  <b-form-textarea v-if="inputType === 'textarea'"
                                  v-model="inputVal"
                                  :state="state(errors)"
                                  :required="required"
                                  :disabled="!isEditing"
                                  :type="type"
                                  ref="input"
                  >
                  </b-form-textarea>
                  <div v-if="inputType === 'dataList'">
                    <b-form-input list="input-list"
                                  id="input-with-list"
                                  v-model="inputVal"
                                  :disabled="!isEditing"
                                  autocomplete="off">
                    </b-form-input>
                    <b-form-datalist id="input-list" :options="classOptions"></b-form-datalist>
                  </div>
                  <BusinessRegistrationCertificate
                    v-if="inputType === 'brc'"
                    v-model="inputVal"
                    rules="required|brc"
                    :disabled="!isEditing"
                    required
                    ref="input"
                    style="margin-left: 6px;"
                  >
                  </BusinessRegistrationCertificate>
                  <TagAndSubTagsList
                    v-if="inputType === 'businessNature'"
                    id="tags"
                    v-model="inputVal"
                    :disabled="!isEditing"
                    :tag-options="merchantTagOptions"
                    :sub-tag-options="merchantSubTagOptions"
                  ></TagAndSubTagsList>
                  <AGCCheckbox
                    v-if="inputType === 'tick'"
                    v-model="inputVal"
                    :state="state(errors)"
                    :required="required"
                    :disabled="!isEditing"
                  ></AGCCheckbox>
                    <DropZone
                      v-if="inputType === 'dropzone'"
                      v-model="inputVal"
                      :max-files="1"
                      :read-only="!isEditing"
                      class="mb-3"
                      upload-type="merchantBrc"
                      acceptedFiles=".jpg,.jpeg,.png,.pdf"
                      required
                    ></DropZone>
                    <DropZone
                      v-if="inputType === 'images'"
                      v-model="inputVal"
                      :read-only="!isEditing"
                      :max-files="3"
                      rules="required"
                      name="圖片集"
                      class="mb-3"
                      upload-type="merchantImages"
                      label="圖片集"
                      id="images"
                      sub-label="(最多3張，每張建議上限2MB，尺寸 600×600px)"
                    ></DropZone>
                  <b-form-select
                    v-if="inputType === 'dropdown'"
                    v-model="inputVal"
                    :options="options"
                    rules="required"
                    :disabled="!isEditing"
                  ></b-form-select>
                  <b-form-invalid-feedback :state="state(errors)">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                  <CompanyIntroTemplateContentEditor
                    v-if="inputType === 'editorWithCompanyIntroTemplate'"
                    v-model="inputVal"
                    :options="options"
                    :is-show-select="isEditing"
                    :disabled="!isEditing"
                  ></CompanyIntroTemplateContentEditor>
                   <AGCFormTags
                      v-if="inputType === 'formTag'"
                      v-model="inputVal"
                      input-id="tags-basic"
                      :validate-reg-ex="validateRegEx"
                      :rules="rules"
                      :placeholder="placeholder"
                      class="mb-3"
                      :name="name"
                      :disabled="!isEditing"
                    >
                    <template v-slot:invalidTag>
                       <slot name="invalidTag">{{ name }} 無效</slot>
                    </template>
                    </AGCFormTags>
                </div>
                <div class="col-lg-3 col-2"
                    :class="{'col-12': isEditing}">
                  <div class="text-lg-right text-center mt-2"
                    v-if="(role === 'admin' || role === 'sale') &&
                    !$route.path.startsWith(`/${role}/salesorder`) ? true : enableEdit"
                  >
                    <div v-if="!isEditing && enableEdit">
                      <b-button variant="light desktop-edit" @click="handleEditGroup()">
                        編輯
                      </b-button>
                      <b-button variant="none mobile-edit" @click="handleEditGroup()">
                        <b-icon-pencil-fill variant="secondary"
                                            font-scale="0.95">
                        </b-icon-pencil-fill>
                      </b-button>
                    </div>
                    <div class="edit-group" v-if="isEditing && enableEdit">
                      <b-button variant="primary"
                                :disabled="failed || loading"
                                type="submit"
                                v-stream:click="onSubmit$">
                        儲存
                      </b-button>
                      <b-button variant="light" @click="cancel">
                        取消
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </b-form-group>
          </ValidationProvider>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { switchMap, tap } from 'rxjs/operators';
import {
  BButton,
  BFormInput,
  BFormDatalist,
  BFormTextarea,
  BFormGroup,
  BFormInvalidFeedback,
  BFormSelect,
  BIconPencilFill,
} from 'bootstrap-vue';
import TagAndSubTagsList from '@/components/merchant/TagAndSubTagsList.vue';
import AGCCheckbox from '@/components/form/AGCCheckbox.vue';
import BusinessRegistrationCertificate from '@/components/BusinessRegistrationCertificate.vue';
import CompanyIntroTemplateContentEditor from '@/components/form/company-intro-template/ContentEditor.vue';
import DropZone from '@/components/form/DropZone.vue';
import AGCFormTags from '@/components/form/AGCFormTags.vue';
import cloneDeep from 'lodash.clonedeep';
import http from '../../helpers/http';
import { getErrorMsg } from '../../helpers/error-msg';
import formMixin from '../../mixins/form';
import toastSettings from '../../helpers/toast-settings';
import agcFormElementMixin from '../../mixins/agc-form-element';

export default {
  props: {
    classOptions: Array,
    options: Array,
    merchantTagOptions: Array,
    merchantSubTagOptions: Object,
    readOnly: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [agcFormElementMixin, formMixin],
  computed: {
    ...mapGetters(['role']),
    ...mapState(['user']),
    apiUrl() {
      if (this.role === 'admin' && this.$route.path.startsWith('/admin/salesorder')) {
        return '/agc-admin-api/salesorder/updateRemarks';
      }
      if (this.role === 'sale' && this.$route.path.startsWith('/sale/salesorder')) {
        return '/agc-sale-api/salesorder/updateRemarks';
      }
      return (this.role === 'admin' || this.role === 'sale') ? `/agc-${this.role}-api/merchants/update` : '/agc-merchant-api/setting/update';
    },
    merchantId() {
      return (this.role === 'admin' || this.role === 'sale') ? this.$route.params.id : this.user.id;
    },
    redemptionId() {
      return this.$route.params.id;
    },
  },
  data() {
    return {
      isEditing: !this.readOnly,
      cachedInputVal: '',
      loading: false,
    };
  },
  watch: {
    readOnly(newVal) {
      this.isEditing = !newVal;
    },
  },
  components: {
    AGCFormTags,
    TagAndSubTagsList,
    AGCCheckbox,
    BusinessRegistrationCertificate,
    BButton,
    BFormInput,
    BFormTextarea,
    BFormGroup,
    BFormInvalidFeedback,
    BFormDatalist,
    CompanyIntroTemplateContentEditor,
    DropZone,
    BFormSelect,
    BIconPencilFill,
  },
  domStreams: ['onSubmit$'],
  subscriptions() {
    return {
      submit: this.onSubmit$
        .pipe(
          switchMap(this.validate),
          tap(() => {
            this.loading = true;
          }),
          switchMap(() => http({
            method: 'POST',
            url: this.apiUrl,
            data: {
              fieldName: this.fieldName,
              value: this.inputVal,
              merchantId: this.merchantId,
              redemptionId: this.redemptionId,
            },
          }, {
            error: ({ data: { msg } }) => {
              this.$bvToast.toast(getErrorMsg(msg, { DUPLICATED: `${this.label}已重覆，請重新輸入` }), {
                ...toastSettings,
                title: '錯誤',
                toaster: 'b-toaster-top-center',
              });
            },
            next: () => {
              this.$emit('is-save');
              this.isEditing = false;
              this.cachedInputVal = cloneDeep(this.inputVal);
            },
            complete: () => {
              this.loading = false;
            },
          })),
        ),
    };
  },
  mounted() {
    this.cachedInputVal = cloneDeep(this.inputVal);
  },
  methods: {
    handleEditGroup() {
      this.isEditing = !this.isEditing;
      const targetInputElement = this.$refs.input?.$el;
      if (targetInputElement) {
        this.$nextTick(() => {
          if (targetInputElement.firstChild) {
            targetInputElement.firstChild.focus();
          } else {
            targetInputElement.focus();
          }
        });
      }
    },
    cancel() {
      this.inputVal = cloneDeep(this.cachedInputVal);
      this.isEditing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.red-dot {
  color: #f54b4e;
}

.input-left{
  box-sizing:border-box;
  padding:10px 0px !important;
}

select {
  &[disabled=disabled] {
    opacity: 1;
    color:#495057;
    border: none;
    background: none;
  }
}

button {
  &.mobile-edit {
    display: none;
    @media (max-width: 675px) {
      display: inline-block;
    }
  }
  &.desktop-edit {
    @media (max-width: 675px) {
      display: none;
    }
  }
}

.edit-group {
  button {
    margin-left: 5px;
  }
  @media (max-width: 576px) {
    display: flex;
    flex-wrap: wrap;
    column-gap: 10px;

    button {
      margin: 0px;
      flex: 1;
    }
  }
}

::v-deep {
  .custom-control-input ~ .custom-control-label::before, ::after {
    left: 0;
  }
}
</style>
